import { useEffect, useMemo, useRef } from 'react';
import settings from 'services/app-settings';
import { useAuthFetch, useFeatureFlags, useUserLevelSettings } from '@cko/dashboard-shared';
import { useLocation, matchPath } from 'react-router-dom';
import { FeatureFlagId } from '../../../../config/shared/enums/featureFlagIds';
import { SUPPORT_BOT_HIDDEN_PATHS } from './constants';
const supportBotKey = settings.support.botKey;
const supportSignedJWTUrl = settings.support.signedJWTUrl;
const supportTokenFieldId = settings.support.tokenFieldId;

export const useSupportBot = () => {
  const fetch = useAuthFetch();
  const { getFeatureFlag } = useFeatureFlags();
  const { currentUser } = useUserLevelSettings();
  const location = useLocation();
  const isSupportBotEnabled = getFeatureFlag(FeatureFlagId.SUPPORT_BOT);
  const expiresInTime = useRef(0);
  const timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const fullName = useMemo(() => (currentUser ? currentUser.fullName : ''), [currentUser]);

  const isHiddenPath = SUPPORT_BOT_HIDDEN_PATHS.some(
    (path) => matchPath(location.pathname, { path, exact: true })?.isExact || false,
  );

  const showHideWidget = () => {
    if (window.zE) {
      window.zE('messenger', isHiddenPath ? 'hide' : 'show');
    }
  };

  useEffect(() => {
    let script: HTMLScriptElement | undefined;
    const getSignedJWTAndLoginUser = async () => {
      if (fullName) {
        try {
          const response = await fetch(`${supportSignedJWTUrl}/token`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json; charset=UTF-8',
            },
            body: JSON.stringify({ name: currentUser?.fullName }),
          });
          const data = await response.json();
          expiresInTime.current = (data.expires_in - 60) * 1000;

          if (window.zE && data.token) {
            window.zE('messenger', 'loginUser', function (loginUser: any) {
              if (window.zE) {
                loginUser(data.token);
                window.zE('messenger:set', 'conversationFields', [
                  { id: supportTokenFieldId, value: data.id }
                ], showHideWidget);
              }
            });
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error fetching signed JWT');
        }
      }
    };

    if (supportBotKey && isSupportBotEnabled && fullName) {
      const initSupportBot = () => {
        if (window.zE) {
          window.zE('messenger', 'hide');

          window.zE('messenger:on', 'open', function () {
            timeout.current = setTimeout(getSignedJWTAndLoginUser, expiresInTime.current);
          });

          window.zE('messenger:on', 'close', function () {
            if (timeout.current) {
              clearTimeout(timeout.current);
            }
          });
        }
      };

      script = document.createElement('script');
      script.id = 'ze-snippet';
      script.src = `https://static.zdassets.com/ekr/snippet.js?key=${supportBotKey}`;
      script.onload = () => {
        initSupportBot();
        getSignedJWTAndLoginUser();
      };
      document.body.appendChild(script);
    }

    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }

      if (window.zE) {
        window.zE('messenger', 'hide');
        window.zE('messenger', 'logoutUser');
      }

      script?.remove();

      delete window.zE;
      delete window.zEACLoaded;
      delete window.zEWebpackACJsonp;
      delete window.zEmbed;
    };
  }, [fullName]);

  useEffect(() => {
    if (window.zE) {
      if (isHiddenPath) {
        window.zE('messenger', 'hide');
      } else {
        window.zE('messenger', 'show');
      }
    }
  }, [location]);

  if (!supportBotKey) {
    // eslint-disable-next-line no-console
    console.warn('[Support Bot] missing bot key');
  }
};
